import { Loader } from '@/components/layout/loading';
import { Wizard } from '@/components/wizard/Wizard';
import { useDAL } from '@/contexts/useDAL';
import { useEnvironment } from '@/contexts/useEnvironment';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { useWorkspace } from '@/contexts/useWorkspace';

import { azureOnboardingFlow } from './azureOnboardingFlow';
import type { AzureOnboardingState } from './azureOnboardingState';

export function AzureOnboardingWizard({
  onOnboardingFlowFinish,
}: {
  onOnboardingFlowFinish: (cloudAccountId: string) => Promise<void>;
}) {
  const { rightPanel } = useWorkspace();
  const dal = useDAL();
  const { data: tenants, isLoading } =
    dal.cloudAccounts.azureApps.useListTenants();
  const { OnboardTenantAgainEnabled } = useFeatureFlags();
  const { isDev, isDemo } = useEnvironment();

  const onFinish = async (state: AzureOnboardingState) => {
    const cloudAccountId =
      state.sourceConnectScope === 'subscription'
        ? state.sourceSubscriptionId
        : (state.sourceManagementGroupId ?? '');
    await onOnboardingFlowFinish(cloudAccountId);
    return false;
  };

  if (isLoading) {
    return <Loader />;
  }
  const tenantId = isDev || isDemo ? '' : tenants?.tenantIds[0] || '';
  const tenantOnboarded = !!tenantId;

  return (
    <Wizard<AzureOnboardingState>
      hideCloseButton={false}
      onAbort={() => rightPanel.setIsOpen(false)}
      hideBackArrow={false}
      onFinish={onFinish}
      flow={[azureOnboardingFlow]}
      initialStep={{
        stepIndex: tenantOnboarded ? 1 : 0,
        keepPreviousSteps: OnboardTenantAgainEnabled,
      }}
      initialState={{
        tenantConnectMethod: 'terraform',
        sourceConnectMethod: 'terraform',
        restore_app_name: 'EonRestoreApplication',
        sourceConnectScope: 'subscription',
        enableScopeSelection: true,
        subscriptionId: '',
        backup_app_service_principal_id: '',
        restore_app_application_id: '',
        restore_app_service_principal_id: '',
        managed_identity_resource_id: '',
        tenantId,
        sourceSubscriptionId: '',
        sourceManagementGroupId: '',
        onboardingFlow: true,
      }}
      title='Connect Azure environment'
      stepperLabels={['Management access', 'Source connection']}
    />
  );
}
